// File: src/components/fans/PointsBreakdownModal.js

import React, { useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useProfile } from "../../context/ProfileContext";
import closeIcon from "../../assets/close.png";
import tipIcon from "../../assets/tip_icon.png";
import tipWhiteIcon from "../../assets/tip_white.png";

const TIER_CONFIG = {
  freemium: { dailyCap: 100, label: "Freemium Tier" },
  tier1: { dailyCap: 1000, label: "Tier 1 Supporter" },
  tier2: { dailyCap: Infinity, label: "Tier 2 VIP (Unlimited)" },
};

/**
 * PointsBreakdownModal Popover
 * Adapts dynamically between Listener and Artist accounts.
 */
const PointsBreakdownModal = ({ isOpen, onClose }) => {
  const { userProfile, activeProfile, isDarkMode } = useProfile();
  const popoverRef = useRef(null);

  // Click-Outside Listener to dismiss popover
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // 🌟 Determine if active profile is an Artist Account
  const isArtistAccount = activeProfile?.type === 'artist';

  // 🌟 Point Resolution Logic based on Profile Type
  const pointsBalance = isArtistAccount 
    ? (activeProfile?.points || 0) 
    : (userProfile?.points || 0);

  const lifetimePoints = isArtistAccount
    ? (activeProfile?.lifetimePoints || pointsBalance)
    : (userProfile?.lifetimePoints || pointsBalance);

  const dailyPointsEarned = userProfile?.dailyPointsEarned || 0;

  const tierKey =
    userProfile?.subscriptionTier ||
    userProfile?.premiumSubscription?.tier ||
    "freemium";
  const userTier = TIER_CONFIG[tierKey] || TIER_CONFIG.freemium;
  const dailyCap = userTier.dailyCap;

  const capProgressPercent =
    dailyCap === Infinity
      ? 100
      : Math.min(100, Math.round((dailyPointsEarned / dailyCap) * 100));

  return (
    <AnimatePresence>
      <motion.div
        ref={popoverRef}
        initial={{ opacity: 0, scale: 0.85, y: -8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.85, y: -8 }}
        transition={{ type: "spring", stiffness: 450, damping: 28 }}
        className={`absolute top-12 right-0 z-[100] w-[320px] rounded-3xl p-5 border shadow-2xl backdrop-blur-md transition-colors ${
          isDarkMode
            ? "bg-zinc-950/95 border-zinc-800 text-white shadow-black/90"
            : "bg-white/95 border-slate-200 text-slate-900 shadow-slate-300"
        }`}
      >
        {/* Close Button */}
        <button
          type="button"
          onClick={onClose}
          className={`absolute top-4 right-4 p-1.5 rounded-full border transition-colors ${
            isDarkMode
              ? "border-zinc-800 bg-zinc-900/80 hover:bg-zinc-800 text-zinc-400 hover:text-white"
              : "border-gray-200 bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-black"
          }`}
        >
          <img src={closeIcon} alt="Close" className="w-3 h-3" />
        </button>

        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2.5 rounded-2xl bg-brand-pink/10 border border-brand-pink/20">
            <img
              src={isDarkMode ? tipWhiteIcon : tipIcon}
              alt="Points"
              className="w-5 h-5 object-contain"
            />
          </div>
          <div>
            <h2 className="text-sm font-black uppercase tracking-wider">
              Points Breakdown
            </h2>
            <p className="text-[11px] text-gray-400 font-medium">
              {isArtistAccount ? "Artist Account Balance" : userTier.label}
            </p>
          </div>
        </div>

        {/* Points Stats Grid */}
        <div className="grid grid-cols-2 gap-2.5 mb-2">
          {/* Available Balance */}
          <div
            className={`p-3 rounded-2xl border ${
              isDarkMode
                ? "bg-zinc-900/80 border-zinc-850"
                : "bg-gray-50 border-gray-150"
            }`}
          >
            <span className="text-[9px] font-bold uppercase tracking-wider text-gray-400 block mb-0.5">
              Available
            </span>
            <span className="text-base font-black text-brand-pink">
              {pointsBalance.toLocaleString()}
            </span>
          </div>

          {/* Lifetime Points */}
          <div
            className={`p-3 rounded-2xl border ${
              isDarkMode
                ? "bg-zinc-900/80 border-zinc-850"
                : "bg-gray-50 border-gray-150"
            }`}
          >
            <span className="text-[9px] font-bold uppercase tracking-wider text-gray-400 block mb-0.5">
              Lifetime Total
            </span>
            <span className="text-base font-black">
              {lifetimePoints.toLocaleString()}
            </span>
          </div>
        </div>

        {/* 🌟 RENDER DAILY CAP SECTION ONLY FOR LISTENER ACCOUNTS */}
        {!isArtistAccount ? (
          <div
            className={`mt-2 p-3.5 rounded-2xl border ${
              isDarkMode
                ? "bg-zinc-900/80 border-zinc-850"
                : "bg-gray-50 border-gray-150"
            }`}
          >
            <div className="flex justify-between items-center mb-1.5">
              <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
                Daily Cap Progress
              </span>
              <span className="text-[11px] font-black text-brand-pink">
                {dailyCap === Infinity
                  ? "Unlimited"
                  : `${dailyPointsEarned.toLocaleString()} / ${dailyCap.toLocaleString()}`}
              </span>
            </div>

            {/* Progress Bar */}
            <div
              className={`w-full h-2.5 rounded-full overflow-hidden border ${
                isDarkMode
                  ? "bg-zinc-800 border-zinc-700"
                  : "bg-gray-200 border-gray-300"
              }`}
            >
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${capProgressPercent}%` }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                className="h-full bg-gradient-to-r from-pink-500 to-[#ff00cc] rounded-full"
              />
            </div>

            <p className="text-[9px] text-gray-400 mt-1.5 text-center font-medium">
              {dailyCap === Infinity
                ? "You have unlimited daily earnings!"
                : capProgressPercent >= 100
                ? "🎉 You have reached today's earning cap!"
                : `${(dailyCap - dailyPointsEarned).toLocaleString()} points remaining today.`}
            </p>
          </div>
        ) : (
          /* Informative banner for Artist Accounts */
          <div
            className={`mt-2 p-3 rounded-2xl border text-center ${
              isDarkMode
                ? "bg-zinc-900/50 border-zinc-800 text-zinc-400"
                : "bg-gray-50 border-gray-200 text-gray-500"
            }`}
          >
            <p className="text-[10px] font-medium">
              Artist accounts earn points directly from listener support and tips.
            </p>
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
};

export default PointsBreakdownModal;